import { useState, useEffect, useRef } from 'react';
import { Flex, Box, Text, Button, VStack, HStack, InputProps } from '@chakra-ui/react';
import { NovaIcon } from 'components/ui/novaIcon';
import { Breakpoints } from 'types/breakpoints';
import { useBreakpoint } from 'hooks/utils/layout/useBreakpoint';
import { ChevronDownIcon, FilesIcon } from 'assets/icons/v2';
import { DefaultOption } from './FilterOptions';
import { BaseFilterOptionProps, VoucherFilterOptionProps } from './types/FilterOption';
import { InputOption } from './FilterOptions/InputOption';

type FilterOptionType = BaseFilterOptionProps & Partial<VoucherFilterOptionProps>;

/**
 * Configuration for a single filter
 */
type FilterConfig = {
  /** Unique identifier for the filter */
  id: string;
  /** Placeholder text shown when no option is selected */
  placeholder: string;
  /** Currently selected value */
  value: string;
  /** Available options for the filter */
  options: FilterOptionType[];
  /** Custom component to render filter options (optional) */
  element?: React.ComponentType<BaseFilterOptionProps | VoucherFilterOptionProps>;
  /** Whether the filter is disabled */
  disabled?: boolean;
  /** Callback function called when filter value changes */
  onChange: (value: string) => void;
  /** Icon to display next to the filter */
  icon?: React.ReactElement;
  /** Callback function to clear the filter value */
  onClear?: () => void;
  /** Whether to hide the placeholder */
  hidePlaceholder?: boolean;
  /** Whether to show the placeholder as the first option */
  showPlaceholderAsFirstOption?: boolean;
  /** Color of the placeholder text */
  placeholderColor?: InputProps['color'];
};

type FiltersProps = {
  /** Array of filter rows, where each row contains an array of filters */
  filters: FilterConfig[][];
};

/**
 * Filters component that renders a collection of filter controls
 *
 * This component displays filters in a responsive layout, supporting both desktop and mobile views.
 * Filters can be arranged in rows, and each filter can have different types of options.
 *
 * @param filters - Array of filter rows, where each row contains an array of filter configurations
 * @returns React element representing the filters UI
 *
 * @example
 * ```tsx
 * <Filters
 *   filters={[
 *     [
 *       {
 *         id: 'category',
 *         placeholder: 'Select category',
 *         value: selectedCategory,
 *         options: categoryOptions,
 *         onChange: setSelectedCategory,
 *         icon: <CategoryIcon />
 *       }
 *     ],
 *     [
 *       {
 *         id: 'status',
 *         placeholder: 'Select status',
 *         value: selectedStatus,
 *         options: statusOptions,
 *         onChange: setSelectedStatus
 *       }
 *     ]
 *   ]}
 * />
 * ```
 */
export const Filters = ({ filters }: FiltersProps): React.ReactElement => {
  const isDesktop = useBreakpoint(Breakpoints.DESKTOP);
  const [openFilterId, setOpenFilterId] = useState<string | null>(null);
  const containerRefs = useRef<Record<string, HTMLDivElement | null>>({});

  /**
   * Enhances filter options by adding a placeholder option when a value is selected
   *
   * @param filter - The filter configuration
   * @returns Enhanced array of filter options
   */
  const getEnhancedOptions = (filter: FilterConfig): FilterOptionType[] => {
    if (filter.options.length === 0) return [];
    if (filter.showPlaceholderAsFirstOption)
      return [
        {
          id: '',
          label: filter.placeholder,
          placeholderColor: filter.placeholderColor,
        },
        ...filter.options,
      ];

    const shouldShowPlaceholder = filter.value && filter.value !== '' && !filter.hidePlaceholder;
    return shouldShowPlaceholder
      ? [
          {
            id: '',
            label: filter.placeholder,
            placeholderColor: filter.placeholderColor,
          },
          ...filter.options,
        ]
      : filter.options;
  };

  /**
   * Handles clicking outside of filter dropdowns to close them
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (openFilterId && !containerRefs.current[openFilterId]?.contains(event.target as Node)) {
        setOpenFilterId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openFilterId]);

  const handleSelect = (filterId: string, optionId: string): void => {
    const filter: FilterConfig | undefined = filters.flat().find((f) => f.id === filterId);
    if (filter) {
      const selectedOption = filter.options.find((x) => x.id === optionId);
      if (selectedOption?.disabled) return;
      filter.onChange(optionId);
      setOpenFilterId(null);
    }
  };

  return (
    <VStack gap="m" width="100%">
      {filters.map((row, rowIndex) => (
        <Flex
          key={`filter-row-${row.map((filter) => filter.id).join('-')}`}
          gap={{ base: 'xs', md: 'm' }}
          width="100%"
          flexDirection={isDesktop ? 'row' : 'column'}
        >
          {row.map((filter) => {
            const selectedOption = filter.value
              ? filter.options.find((opt) => opt.id === filter.value)
              : undefined;

            return (
              <Box
                key={filter.id}
                position="relative"
                flex={{ base: 'unset', md: 1 }}
                width={{ base: '100%', md: 'auto' }}
                mb={{ base: 'xs', md: '0' }}
                ref={(el: HTMLDivElement | null) => {
                  containerRefs.current[filter.id] = el;
                }}
              >
                {filter.options.length === 0 ? (
                  <InputOption
                    value={filter.value}
                    placeholder={filter.placeholder}
                    id="filter.id"
                    label={filter.placeholder}
                    onChange={filter.onChange}
                    disabled={filter.disabled}
                    icon={filter.icon}
                    onClear={filter.onClear}
                  />
                ) : (
                  <>
                    <Button
                      onClick={() => setOpenFilterId(openFilterId === filter.id ? null : filter.id)}
                      variant="secondaryOutline"
                      borderColor="border.accent"
                      w="full"
                      py="m"
                      px="m"
                      textAlign="left"
                      justifyContent="space-between"
                      display="flex"
                      disabled={filter?.disabled}
                      fontSize="16px"
                      fontWeight="regular"
                      color="text.primary"
                      zIndex="2"
                      position="relative"
                      _hover={{ bg: 'background.secondary' }}
                      backgroundColor="background.secondary"
                    >
                      <HStack width="100%" justifyContent="space-between">
                        <HStack>
                          {filter.icon || (
                            <NovaIcon icon={FilesIcon} size="m" color="icon.accent" aria-hidden />
                          )}
                          <Text
                            color={
                              selectedOption?.label
                                ? 'text.primary'
                                : (filter.placeholderColor ?? 'text.primary')
                            }
                            lineClamp={1}
                          >
                            {selectedOption?.label.substring(0, 65) || filter.placeholder}
                          </Text>
                        </HStack>
                        {selectedOption?.allowedUsage !== undefined &&
                          selectedOption?.timesUsed !== undefined && (
                            <Text
                              color={
                                selectedOption?.timesUsed < selectedOption?.allowedUsage
                                  ? 'grey.500'
                                  : 'text.error'
                              }
                              textStyle="body.m.regular"
                            >
                              {selectedOption.timesUsed}/{selectedOption.allowedUsage}
                            </Text>
                          )}
                      </HStack>
                      <ChevronDownIcon boxSize="14px" />
                    </Button>
                    {openFilterId === filter.id && (
                      <Box
                        position="absolute"
                        top="0"
                        w="full"
                        bg="grey.white"
                        boxShadow="md"
                        borderRadius="md"
                        maxH="300px"
                        zIndex="1"
                        overflow="hidden"
                      >
                        <Box height="50px" bg="background.primary" />
                        <Box overflowY="auto" maxH="250px">
                          <VStack align="stretch" gap="2xs" w="full">
                            {getEnhancedOptions(filter).map((option) => {
                              const FilterElement = filter.element || DefaultOption;
                              return (
                                <FilterElement
                                  key={option.id}
                                  isActive={option.id === filter.value}
                                  {...option}
                                  onClick={() => {
                                    handleSelect(filter.id, option.id);
                                  }}
                                />
                              );
                            })}
                          </VStack>
                        </Box>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            );
          })}
        </Flex>
      ))}
    </VStack>
  );
};
